import type { createPaymentInput } from "@/api/custom_types";

export const PAYMENT_METHODS = [
	"usdt",
	"paypal",
	"bank_transfer",
	"other",
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export interface PaymentTarget {
	/** Relation being settled, or `null` when paying the platform. */
	relationId: string | null;
	/** Signed-in user, used as the payer of a system payment. */
	userId: string;
	/** Seller's wallet (market) or the platform wallet (system). */
	recipientWallet: string;
}

/**
 * What is still open on a relation, in EUR.
 *
 * `current_debt` is what the buyer owes the seller, and that single number is
 * what both sides act on: the buyer pays it, the seller collects it. A
 * negative balance means nothing is owed in the normal direction, so it reads
 * as zero rather than as a debt pointing the other way.
 */
export const getOutstanding = (currentDebt: unknown) => {
	const debt = Number(currentDebt);
	if (!Number.isFinite(debt)) return 0;
	return Math.max(debt, 0);
};

/**
 * USDT settles onto the *recipient's* registered wallet, and the backend only
 * accepts an automatic market payment from the buyer of that relation. A seller
 * paying their buyer, or a relation with no wallet on file, is manual-only.
 */
export const isUsdtPayable = ({
	isPlatform,
	iAmBuyer,
	recipientWallet,
}: {
	isPlatform: boolean;
	iAmBuyer: boolean;
	recipientWallet?: string | null;
}) => !!recipientWallet && (isPlatform || iAmBuyer);

export const buildPaymentBody = (
	method: PaymentMethod,
	amount: number,
	note: string | null | undefined,
	{ relationId, userId, recipientWallet }: PaymentTarget,
): createPaymentInput["body"] => {
	const isPlatform = relationId === null;

	if (method === "usdt") {
		return isPlatform
			? {
					type: "SYSTEM_AUTOMATIC",
					network: "TRC20",
					user_id: userId,
					claimed_amount: amount,
					system_wallet_address: recipientWallet,
				}
			: {
					type: "MARKET_AUTOMATIC",
					network: "TRC20",
					relation_id: relationId,
					claimed_amount: amount,
					seller_wallet_address: recipientWallet,
				};
	}

	return isPlatform
		? {
				type: "SYSTEM_MANUAL",
				network: "EUR_MANUAL",
				user_id: userId,
				amount,
				manual_payment_method: method,
				payment_note: note ?? null,
			}
		: {
				type: "MARKET_MANUAL",
				network: "EUR_MANUAL",
				relation_id: relationId,
				amount,
				manual_payment_method: method,
				payment_note: note ?? null,
			};
};

/**
 * Balances are kept in EUR; an automatic payment is sent in USDT. Round up to
 * cents so a converted transfer never lands a fraction short of the debt.
 */
export const eurToUsdt = (eur: number, rate: number | undefined) => {
	if (!rate || !Number.isFinite(rate) || rate <= 0) return null;
	return Math.ceil(eur * rate * 100) / 100;
};
